import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import {
  Button,
  Card,
  Checkbox,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Pill,
  PillGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@/components/ui';
import {
  EK_PLATFORMS,
  SALES_PLATFORMS,
  type AdminPriceSummaryQuery,
  type AdminProductPriceQuery,
  type BulkPriceFilter,
  type EkPlatform,
  type MissingPriceFilter,
  type MockupTemplateFilter,
  type PhoenixMappingFilter,
  type PriceRowParams,
  type ReleaseChannel,
  type ReleaseChannelKey,
  type SalesPlatform,
} from '@/api/admin';
import { useAdminDecorationMethods } from '@/hooks/useAdminDecorationMethods';
import {
  useAdminPriceSummary,
  useAdminProductPriceRows,
} from '@/hooks/useAdminProductPrices';
import { useCategories, useManufacturers } from '@/hooks/useProducts';
import { PriceLegend } from '@/components/admin/PriceLegend';
import {
  PLATFORM_LABEL,
  PriceSummaryStrip,
} from '@/components/admin/PriceSummaryStrip';
import {
  FIXED_COLUMN_COUNT,
  ProductPriceRow,
  STICKY_CELL,
  type PriceColumnMethod,
} from '@/components/admin/ProductPriceRow';
import { PriceTableAnnouncer } from '@/components/admin/price-table-announcer';
import { PriceCellActivityProvider } from '@/components/admin/price-cell-activity';
import {
  PriceTableBulkBar,
  type BulkPriceTarget,
} from '@/components/admin/BulkPriceBar';
import { useRowSelection } from '@/components/admin/useRowSelection';
import { releaseChannelTitle } from '@/components/admin/ProductPriceRowCells';
import { cn } from '@/lib/utils';
import {
  ADMIN_PRODUCT_PAGE_SIZE,
  ADMIN_PRODUCT_PARAMS,
  ADMIN_SEARCH_DEBOUNCE_MS,
  DEFAULT_EK_PLATFORM,
  DEFAULT_SALES_PLATFORM,
  MISSING_PRICE_FILTERS,
  MOCKUP_TEMPLATE_FILTERS,
  PHOENIX_MAPPING_FILTERS,
  parseEkPlatform,
  parseIsActive,
  parseMissing,
  parseMockup,
  parsePage,
  parsePhoenix,
  parseReleaseChannel,
  parseSalesPlatform,
  type AdminProductParamName,
} from './admin-list-params';

const P = ADMIN_PRODUCT_PARAMS;

const EK_PLATFORM_LABEL: Record<EkPlatform, string> = {
  shirtee: 'shirtee',
  justdroppit: 'justdroppit',
  none: 'no markup',
};

const MISSING_LABEL: Record<MissingPriceFilter, string> = {
  purchasePrice: 'Blank cost',
  recommendedSalesPrice: 'Sales price',
  decorationPrice: 'Decoration price',
};

const PHOENIX_FILTER_LABEL: Record<PhoenixMappingFilter, string> = {
  mapped: 'Mapped in Phoenix',
  unmapped: 'No Phoenix mapping',
};

const MOCKUP_FILTER_LABEL: Record<MockupTemplateFilter, string> = {
  present: 'Has mockup template',
  missing: 'No mockup template',
};

const SKELETON_ROWS = 6;

/**
 * Methods the price table never shows as a column, by catalogue code:
 * screen print and patches are not priced through this page. Their data
 * stays untouched and the product detail page still manages them.
 */
const HIDDEN_METHOD_CODES: ReadonlySet<string> = new Set([
  'SCREENPRINT',
  'SEWING',
]);

const TOAST_OPTS = { position: 'bottom-right' as const };

/**
 * Admin: products and prices (R9–R15, R18, R19). One row per product with
 * the editable blank cost, recommended sales price, a cell per decoration
 * method of the chosen platform and the computed seller cost; a selection
 * with the bulk bar below. Filters, platform and page live in the URL so
 * the view is shareable; changing a filter restarts at the first page.
 *
 * The providers (live region, cell activity) wrap the content so the bar
 * can read whether a cell is busy.
 */
export function AdminProductsPage() {
  return (
    <PriceTableAnnouncer>
      <PriceCellActivityProvider>
        <AdminProductsPageContent />
      </PriceCellActivityProvider>
    </PriceTableAnnouncer>
  );
}

function AdminProductsPageContent() {
  const [searchParams, setSearchParams] = useSearchParams();

  const urlSearch = searchParams.get(P.search) ?? '';
  const categoryId = searchParams.get(P.categoryId) ?? '';
  const manufacturerId = searchParams.get(P.manufacturerId) ?? '';
  const isActive = parseIsActive(searchParams.get(P.isActive));
  const missing = parseMissing(searchParams.get(P.missing));
  const phoenix = parsePhoenix(searchParams.get(P.phoenix));
  const mockup = parseMockup(searchParams.get(P.mockup));
  const channel = parseReleaseChannel(searchParams.get(P.channel));
  // `released` names the state on `channel`; without a channel it means
  // nothing (and the API would refuse it).
  const released = channel
    ? parseIsActive(searchParams.get(P.released))
    : undefined;
  const platform = parseSalesPlatform(searchParams.get(P.platform));
  const ekPlatform = parseEkPlatform(searchParams.get(P.ekPlatform));
  const page = parsePage(searchParams.get(P.page));

  const [searchInput, setSearchInput] = useState(urlSearch);

  // Follow URL changes from outside the input (nav link, back button)
  useEffect(() => setSearchInput(urlSearch), [urlSearch]);

  useEffect(() => {
    document.title = 'Product Admin – DesignDeck';
    return () => {
      document.title = 'DesignDeck';
    };
  }, []);

  /**
   * Writes one param to the URL. Filters restart at the first page; the
   * seller-cost platform only re-evaluates the rows, so it keeps the page.
   */
  const setParam = (
    name: AdminProductParamName,
    value: string,
    { replace = false, resetPage = true } = {}
  ) => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value) next.set(name, value);
        else next.delete(name);
        if (resetPage) next.delete(P.page);
        return next;
      },
      { replace }
    );
  };

  // Debounced search; only a changed value touches the URL, so the page
  // survives mounting with a prefilled field.
  useEffect(() => {
    const timer = setTimeout(() => {
      const search = searchInput.trim();
      if (search !== urlSearch) {
        setParam(P.search, search, { replace: true });
      }
    }, ADMIN_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only typing starts the debounce
  }, [searchInput]);

  /**
   * Sets the channel filter: a channel with its release state, or neither
   * (clearing the channel always clears `released` with it).
   */
  const setChannelFilter = (
    nextChannel: ReleaseChannelKey | undefined,
    nextReleased: boolean | undefined
  ) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      if (nextChannel) next.set(P.channel, nextChannel);
      else next.delete(P.channel);
      if (nextChannel && nextReleased !== undefined) {
        next.set(P.released, String(nextReleased));
      } else {
        next.delete(P.released);
      }
      next.delete(P.page);
      return next;
    });
  };

  const setPage = (nextPage: number) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      if (nextPage > 1) next.set(P.page, String(nextPage));
      else next.delete(P.page);
      return next;
    });
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchParams((previous) => {
      const next = new URLSearchParams();
      // The platform choices are a view setting, not a filter.
      const keptPlatform = previous.get(P.platform);
      const keptEk = previous.get(P.ekPlatform);
      if (keptPlatform) next.set(P.platform, keptPlatform);
      if (keptEk) next.set(P.ekPlatform, keptEk);
      return next;
    });
  };

  const setPlatform = (next: SalesPlatform) =>
    setParam(P.platform, next === DEFAULT_SALES_PLATFORM ? '' : next);
  const setEkPlatform = (next: EkPlatform) =>
    setParam(P.ekPlatform, next === DEFAULT_EK_PLATFORM ? '' : next, {
      resetPage: false,
    });

  const priceRowParams = useMemo<PriceRowParams>(
    () => ({ ekPlatform, platform }),
    [ekPlatform, platform]
  );

  const summaryQuery = useMemo<AdminPriceSummaryQuery>(
    () => ({
      search: urlSearch || undefined,
      categoryId: categoryId || undefined,
      manufacturerId: manufacturerId || undefined,
      isActive,
      phoenix,
      mockup,
      channel,
      released,
      platform,
      ekPlatform,
    }),
    [
      urlSearch,
      categoryId,
      manufacturerId,
      isActive,
      phoenix,
      mockup,
      channel,
      released,
      platform,
      ekPlatform,
    ]
  );
  const query = useMemo<AdminProductPriceQuery>(
    () => ({ ...summaryQuery, missing, page, limit: ADMIN_PRODUCT_PAGE_SIZE }),
    [summaryQuery, missing, page]
  );

  const {
    data: result,
    isLoading,
    // The previous page stays visible while a new filter or page loads.
    // Its rows belong to the old query, so selecting them must wait.
    isPlaceholderData,
    isError,
    refetch,
  } = useAdminProductPriceRows(query);
  const summary = useAdminPriceSummary(summaryQuery);
  const methods = useAdminDecorationMethods();
  const { data: categories } = useCategories();
  const { data: manufacturers } = useManufacturers();

  const rows = useMemo(() => result?.data ?? [], [result]);
  /** Release channel columns, cards and bulk options, from the API. */
  const releaseChannels = useMemo<ReleaseChannel[]>(
    () => summary.data?.releaseChannels ?? [],
    [summary.data]
  );
  const channelName = (key: ReleaseChannelKey) =>
    releaseChannels.find((candidate) => candidate.key === key)?.name ?? key;
  const meta = result?.meta;

  /**
   * Decoration columns: active methods that have a base price on the
   * platform or are offered by a product on this page (so an offered but
   * unpriced method still shows its orange "no price" cell, R1a), minus
   * the methods in HIDDEN_METHOD_CODES.
   */
  const columns = useMemo<PriceColumnMethod[]>(() => {
    const offeredIds = new Set(
      rows.flatMap((row) => row.offeredMethods.map((m) => m.methodId))
    );
    return (methods.data ?? [])
      .filter(
        (method) =>
          method.isActive !== false && !HIDDEN_METHOD_CODES.has(method.code)
      )
      .map((method) => ({
        method,
        basePrice:
          method.platformPrices.find((row) => row.platform === platform)
            ?.price ?? null,
      }))
      .filter(
        ({ method, basePrice }) =>
          basePrice !== null || offeredIds.has(method.id)
      );
  }, [methods.data, platform, rows]);

  const categoryName = useMemo(
    () => new Map((categories ?? []).map((c) => [c.id, c.name])),
    [categories]
  );
  const manufacturerName = useMemo(
    () => new Map((manufacturers ?? []).map((m) => [m.id, m.name])),
    [manufacturers]
  );

  const activeFilters = [
    urlSearch ? `search "${urlSearch}"` : null,
    categoryId
      ? `category ${categoryName.get(categoryId) ?? categoryId}`
      : null,
    manufacturerId
      ? `manufacturer ${manufacturerName.get(manufacturerId) ?? manufacturerId}`
      : null,
    isActive === undefined ? null : isActive ? 'active only' : 'inactive only',
    missing ? `missing ${MISSING_LABEL[missing].toLowerCase()}` : null,
    phoenix ? PHOENIX_FILTER_LABEL[phoenix].toLowerCase() : null,
    mockup ? MOCKUP_FILTER_LABEL[mockup].toLowerCase() : null,
    channel
      ? `${released === false ? 'not released' : 'released'} on ${channelName(channel)}`
      : null,
  ].filter((filter): filter is string => filter !== null);

  const isEmpty = !isLoading && !isError && rows.length === 0;
  const columnCount =
    FIXED_COLUMN_COUNT + releaseChannels.length + columns.length;

  // ─── Selection and bulk bar (R19) ───────────────────────────────

  // The selection refers to rows of this filter and search; paging and
  // the platform choices only change how the same rows are shown. The one
  // exception is the "missing decoration price" filter, which is evaluated
  // per platform and so names different rows after a platform switch.
  const filterKey = JSON.stringify({
    search: urlSearch,
    categoryId,
    manufacturerId,
    isActive: isActive ?? null,
    missing: missing ?? null,
    phoenix: phoenix ?? null,
    mockup: mockup ?? null,
    channel: channel ?? null,
    released: released ?? null,
    platform: missing === 'decorationPrice' ? platform : null,
  });
  const {
    selection,
    count: selectedCount,
    isSelected,
    toggle,
    togglePage,
    selectAllFiltered,
    clear: clearSelection,
  } = useRowSelection({
    filterKey,
    onReset: () =>
      toast('Selection cleared because the filters changed', TOAST_OPTS),
  });

  const pageIds = useMemo(() => rows.map((row) => row.id), [rows]);
  const pageSelected = pageIds.filter((id) => isSelected(id)).length;
  const pageFullySelected =
    pageIds.length > 0 && pageSelected === pageIds.length;
  const total = meta?.total ?? 0;

  const bulkTarget = useMemo<BulkPriceTarget | null>(() => {
    if (selection.mode === 'ids') {
      return selection.ids.size > 0
        ? { kind: 'ids', productIds: Array.from(selection.ids) }
        : null;
    }
    const filter: BulkPriceFilter = {};
    if (urlSearch) filter.search = urlSearch;
    if (categoryId) filter.categoryId = categoryId;
    if (manufacturerId) filter.manufacturerId = manufacturerId;
    if (isActive !== undefined) filter.isActive = isActive;
    if (missing) {
      filter.missing = missing;
      if (missing === 'decorationPrice') filter.platform = platform;
    }
    if (phoenix) filter.phoenix = phoenix;
    if (mockup) filter.mockup = mockup;
    if (channel) {
      filter.channel = channel;
      if (released !== undefined) filter.released = released;
    }
    // Fail closed on the API: without a narrowing field only `all` targets
    // the whole catalogue.
    if (Object.keys(filter).length === 0) filter.all = true;
    if (selection.excludedIds.size > 0) {
      filter.excludedIds = Array.from(selection.excludedIds);
    }
    return { kind: 'filter', filter };
  }, [
    selection,
    urlSearch,
    categoryId,
    manufacturerId,
    isActive,
    missing,
    phoenix,
    mockup,
    channel,
    released,
    platform,
  ]);

  return (
    <div className="mx-auto flex w-full max-w-[1920px] flex-col gap-6">
      <PageHeader
        eyebrow="Admin"
        title="Products and prices"
        description="Blank cost, recommended sales price and decoration prices per product. Cells save on their own; the seller cost follows."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
        <FormField label="Search" className="xl:col-span-2">
          <Input
            type="search"
            icon={Search}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Name or article number"
          />
        </FormField>
        <FormField label="Category">
          <Select
            value={categoryId}
            onChange={(e) => setParam(P.categoryId, e.target.value)}
          >
            <option value="">All categories</option>
            {(categories ?? []).map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Manufacturer">
          <Select
            value={manufacturerId}
            onChange={(e) => setParam(P.manufacturerId, e.target.value)}
          >
            <option value="">All manufacturers</option>
            {(manufacturers ?? []).map((manufacturer) => (
              <option key={manufacturer.id} value={manufacturer.id}>
                {manufacturer.name}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Status">
          <Select
            value={isActive === undefined ? '' : String(isActive)}
            onChange={(e) => setParam(P.isActive, e.target.value)}
          >
            <option value="">All products</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </Select>
        </FormField>
        <FormField label="Missing price">
          <Select
            value={missing ?? ''}
            onChange={(e) => setParam(P.missing, e.target.value)}
          >
            <option value="">Any</option>
            {MISSING_PRICE_FILTERS.map((option) => (
              <option key={option} value={option}>
                {MISSING_LABEL[option]}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Phoenix">
          <Select
            value={phoenix ?? ''}
            onChange={(e) => setParam(P.phoenix, e.target.value)}
          >
            <option value="">Any</option>
            {PHOENIX_MAPPING_FILTERS.map((option) => (
              <option key={option} value={option}>
                {PHOENIX_FILTER_LABEL[option]}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Mockup template">
          <Select
            value={mockup ?? ''}
            onChange={(e) => setParam(P.mockup, e.target.value)}
          >
            <option value="">Any</option>
            {MOCKUP_TEMPLATE_FILTERS.map((option) => (
              <option key={option} value={option}>
                {MOCKUP_FILTER_LABEL[option]}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Channel">
          <Select
            value={channel ?? ''}
            onChange={(e) =>
              setChannelFilter(
                parseReleaseChannel(e.target.value),
                // Keep the chosen release state when switching channels.
                released
              )
            }
          >
            <option value="">All channels</option>
            {releaseChannels.map((option) => (
              <option
                key={option.key}
                value={option.key}
                title={releaseChannelTitle(option)}
              >
                {releaseChannelTitle(option)}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Release">
          <Select
            value={channel ? String(released !== false) : ''}
            disabled={!channel}
            title={channel ? undefined : 'Choose a channel first'}
            onChange={(e) =>
              setChannelFilter(channel, e.target.value === 'true')
            }
          >
            {!channel && <option value="">Choose a channel</option>}
            <option value="true">Released</option>
            <option value="false">Not released</option>
          </Select>
        </FormField>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-ink">
            Decoration prices for
          </span>
          <PillGroup aria-label="Platform">
            {SALES_PLATFORMS.map((option) => (
              <Pill
                key={option}
                active={option === platform}
                onClick={() => setPlatform(option)}
              >
                {PLATFORM_LABEL[option]}
              </Pill>
            ))}
          </PillGroup>
        </div>
        <Button asChild variant="outline">
          <Link to="/admin/products/frame-review">Rahmen-Durchlauf</Link>
        </Button>
        <Button asChild variant="outline">
          <Link to="/admin/decoration-methods">Decoration methods</Link>
        </Button>
      </div>

      {!summary.isError && (
        <PriceSummaryStrip
          summary={summary.data}
          isLoading={summary.isLoading}
          platform={platform}
          missing={missing}
          onMissingChange={(next) => setParam(P.missing, next ?? '')}
          status={isActive}
          onStatusChange={(next) =>
            setParam(P.isActive, next === undefined ? '' : String(next))
          }
          channel={channel}
          released={released}
          onChannelChange={(next) =>
            setChannelFilter(next, next ? true : undefined)
          }
        />
      )}

      {isError && (
        <Card className="flex flex-col items-center gap-4 text-center">
          <p className="text-sm text-danger">Failed to load products.</p>
          <Button variant="outline" onClick={() => void refetch()}>
            Retry
          </Button>
        </Card>
      )}

      {isEmpty &&
        (activeFilters.length > 0 ? (
          <EmptyState
            title="No products match"
            description={`Active filters: ${activeFilters.join(', ')}.`}
            action={
              <Button variant="outline" onClick={clearFilters}>
                Clear filters
              </Button>
            }
          />
        ) : (
          <EmptyState
            title="No products yet"
            description="Products appear here once a sync has imported them."
          />
        ))}

      {!isError &&
        !isEmpty &&
        pageFullySelected &&
        selection.mode === 'ids' && (
          <p className="text-sm text-ink-2" data-testid="select-all-hint">
            All {pageIds.length} products on this page are selected.
            {total > pageIds.length && (
              <>
                {' '}
                <button
                  type="button"
                  disabled={isPlaceholderData}
                  onClick={() => selectAllFiltered(total)}
                  className="font-semibold text-ink underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  Select all {total} filtered
                </button>
              </>
            )}
          </p>
        )}

      {!isError && !isEmpty && (
        <Table
          density="compact"
          aria-busy={isLoading || isPlaceholderData || undefined}
        >
          <TableHead>
            <tr>
              <TableHeaderCell className={STICKY_CELL.select}>
                <Checkbox
                  aria-label="Select all on this page"
                  checked={pageFullySelected}
                  indeterminate={pageSelected > 0 && !pageFullySelected}
                  disabled={
                    isLoading || isPlaceholderData || pageIds.length === 0
                  }
                  onChange={() => togglePage(pageIds)}
                />
              </TableHeaderCell>
              <TableHeaderCell className={STICKY_CELL.active}>
                Active
              </TableHeaderCell>
              <TableHeaderCell className={STICKY_CELL.product}>
                Product
              </TableHeaderCell>
              <TableHeaderCell>Phoenix</TableHeaderCell>
              <TableHeaderCell>End of life</TableHeaderCell>
              {releaseChannels.map((releaseChannel) => (
                <TableHeaderCell
                  key={releaseChannel.key}
                  data-channel-key={releaseChannel.key}
                  title={releaseChannelTitle(releaseChannel)}
                  className="w-20 whitespace-normal text-center leading-tight"
                >
                  {releaseChannel.name}
                </TableHeaderCell>
              ))}
              <TableHeaderCell>Variants</TableHeaderCell>
              <TableHeaderCell className="whitespace-nowrap">
                Carry-over
              </TableHeaderCell>
              <TableHeaderCell>Blank cost</TableHeaderCell>
              <TableHeaderCell>Rec. sales price</TableHeaderCell>
              <TableHeaderCell>
                <div className="flex flex-col gap-1">
                  <span>Seller cost</span>
                  <select
                    aria-label="Seller cost platform"
                    value={ekPlatform}
                    onChange={(e) =>
                      setEkPlatform(e.target.value as EkPlatform)
                    }
                    className="h-7 rounded-control bg-paper px-1.5 text-xs font-normal normal-case tracking-normal text-ink ring-1 ring-inset ring-line focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    {EK_PLATFORMS.map((option) => (
                      <option key={option} value={option}>
                        {EK_PLATFORM_LABEL[option]}
                      </option>
                    ))}
                  </select>
                </div>
              </TableHeaderCell>
              {columns.map(({ method, basePrice }) => (
                <TableHeaderCell
                  key={method.id}
                  data-method-id={method.id}
                  className="whitespace-nowrap"
                >
                  <span className="block">{method.name}</span>
                  <span
                    className={cn(
                      'block font-mono text-[11px] font-normal normal-case tracking-normal',
                      basePrice === null ? 'text-warning' : 'text-mute'
                    )}
                  >
                    {basePrice === null ? 'no base price' : `base ${basePrice}`}
                  </span>
                </TableHeaderCell>
              ))}
              <TableHeaderCell className="whitespace-nowrap">
                Last price change
              </TableHeaderCell>
            </tr>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from({ length: SKELETON_ROWS }, (_, index) => (
                  <TableRow key={index} data-testid="skeleton-row">
                    {Array.from({ length: columnCount }, (_, cell) => (
                      <TableCell key={cell}>
                        <span
                          className="block h-5 w-full min-w-[2rem] animate-pulse rounded-chip bg-paper-3"
                          aria-hidden="true"
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : rows.map((row) => (
                  <ProductPriceRow
                    key={row.id}
                    row={row}
                    columns={columns}
                    releaseChannels={releaseChannels}
                    platform={platform}
                    priceRowParams={priceRowParams}
                    categoryName={categoryName.get(row.categoryId)}
                    manufacturerName={
                      row.manufacturerId
                        ? manufacturerName.get(row.manufacturerId)
                        : undefined
                    }
                    leading={
                      <Checkbox
                        aria-label={`Select ${row.name}`}
                        checked={isSelected(row.id)}
                        disabled={isPlaceholderData}
                        onChange={() => toggle(row.id)}
                      />
                    }
                  />
                ))}
          </TableBody>
        </Table>
      )}
      {isLoading && <span className="sr-only">Loading products</span>}

      {meta && meta.totalPages > 1 && (
        <nav
          aria-label="Pagination"
          className="flex flex-wrap items-center justify-center gap-4"
        >
          <Button
            variant="outline"
            onClick={() => setPage(Math.max(1, meta.page - 1))}
            disabled={!meta.hasPreviousPage}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            Previous
          </Button>
          <span className="font-mono text-sm text-mute">
            Page {meta.page} of {meta.totalPages} · {meta.total} products
          </span>
          <Button
            variant="outline"
            onClick={() => setPage(meta.page + 1)}
            disabled={!meta.hasNextPage}
          >
            Next
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </nav>
      )}

      <PriceLegend />

      <PriceTableBulkBar
        count={selectedCount}
        filtered={selection.mode === 'filtered'}
        target={bulkTarget}
        columns={columns}
        releaseChannels={releaseChannels}
        platform={platform}
        onClearSelection={clearSelection}
      />
    </div>
  );
}
